import React, { useState, useEffect, useRef } from 'react'
import {
  Award, CheckCircle, XCircle, Clock, RotateCcw,
  ChevronRight, AlertTriangle, Star, Target, Brain,
  PenTool, BookOpen, Lightbulb,
  ChevronDown, ChevronUp, Trophy, Zap, BarChart2, Youtube,
  Minus, Plus, X
} from 'lucide-react'
import { useProgressStore, PASS_SCORE, useUnitProgress } from '../store/progressStore'
import ConceptVideoModal from '../components/ConceptVideoModal'
import { useVideoStore } from '../store/videoStore'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG,
  type PredictedFragment
} from '../store/predictedFragments'

const UNIT_ID = 'A1-1'

interface TestQuestion {
  id: string
  number: number
  category: string
  difficulty: 'easy' | 'medium' | 'hard'
  question: string
  choices: string[]
  answer: number
  explanation: string[]
  concept: string
  misconception?: string
  misconceptionCode?: string
  learnStepId?: string
}

const TEST_QUESTIONS: TestQuestion[] = [
  {
    id: 'Q01', number: 1, category: '문자식 표현', difficulty: 'easy',
    question: '한 개에 a원인 과자를 4개 샀을 때, 금액을 문자식으로 바르게 나타낸 것은?',
    choices: ['a+4', '4+a', '4a', 'a÷4'],
    answer: 2,
    explanation: [
      '개수×단가 = 4×a = 4a',
      '덧셈(a+4)은 개수와 단가를 더하는 것이라 틀림',
      '4a가 4개 × a원의 올바른 표현 ✓',
    ],
    concept: '문자식 표현', misconceptionCode: 'AM01', learnStepId: 'AA01',
    misconception: '×는 생략하고 숫자를 문자 앞에 씁니다: 4×a = 4a',
  },
  {
    id: 'Q02', number: 2, category: '기호 생략', difficulty: 'easy',
    question: 'a×(-3)을 기호를 생략하여 나타내면?',
    choices: ['-3a', 'a-3', '3a', '-a3'],
    answer: 0,
    explanation: [
      '수와 문자의 곱: 수를 문자 앞에 씀',
      'a×(-3) = (-3)×a = -3a ✓',
      '음수 계수도 문자 앞에 위치',
    ],
    concept: '기호 생략', misconceptionCode: 'AM01', learnStepId: 'AA02',
    misconception: '숫자는 항상 문자 앞! a×(-3) = -3a',
  },
  {
    id: 'Q03', number: 3, category: '기호 생략', difficulty: 'easy',
    question: '1×x를 바르게 나타낸 것은?',
    choices: ['1x', 'x', '0', 'x+1'],
    answer: 1,
    explanation: [
      '계수가 1인 경우 1은 생략',
      '1×x = x ✓',
      '1x라고 쓰면 잘못된 표현',
    ],
    concept: '기호 생략(1 생략)', misconceptionCode: 'AM03', learnStepId: 'AA02',
    misconception: '계수 1은 생략합니다! 1×x = x (1x 아님)',
  },
  {
    id: 'Q04', number: 4, category: '식의 값', difficulty: 'easy',
    question: 'x=3일 때, 2x-5의 값은?',
    choices: ['1', '11', '0', '-1'],
    answer: 0,
    explanation: [
      'x=3 대입: 2×(3)-5',
      '= 6-5 = 1 ✓',
    ],
    concept: '식의 값', misconceptionCode: 'AM04', learnStepId: 'AA03',
    misconception: '대입 후 계산 순서: 곱셈 먼저! 2×3-5 = 6-5 = 1',
  },
  {
    id: 'Q05', number: 5, category: '식의 값(음수)', difficulty: 'easy',
    question: 'a=-2일 때, a²의 값은?',
    choices: ['-4', '4', '-2', '2'],
    answer: 1,
    explanation: [
      'a=-2 대입: (-2)²',
      '음수 대입 시 반드시 괄호!',
      '(-2)² = (-2)×(-2) = 4 ✓',
    ],
    concept: '음수 대입', misconceptionCode: 'AM05', learnStepId: 'AA03',
    misconception: '음수 대입 시 괄호 필수! (-2)²=4 (≠ -2²=-4)',
  },
  {
    id: 'Q06', number: 6, category: '동류항', difficulty: 'easy',
    question: '다음 중 3x와 동류항인 것은?',
    choices: ['3y', '3x²', '-5x', '3'],
    answer: 2,
    explanation: [
      '동류항: 문자와 차수가 같은 항',
      '3x와 -5x → 모두 x¹ → 동류항 ✓',
      '3y: 문자 다름 ✗ | 3x²: 차수 다름 ✗ | 3: 상수항 ✗',
    ],
    concept: '동류항', misconceptionCode: 'AM06', learnStepId: 'AA04',
    misconception: '동류항은 문자와 차수가 모두 같아야 합니다!',
  },
  {
    id: 'Q07', number: 7, category: '동류항 정리', difficulty: 'easy',
    question: '4x+3y-x+2y를 간단히 하면?',
    choices: ['3x+5y', '8xy', '5x+3y', '3x-5y'],
    answer: 0,
    explanation: [
      'x항: 4x-x = 3x',
      'y항: 3y+2y = 5y',
      '→ 3x+5y ✓',
    ],
    concept: '동류항 정리', misconceptionCode: 'AM07', learnStepId: 'AA04',
    misconception: '같은 문자끼리만 모읍니다: x끼리, y끼리 따로!',
  },
  {
    id: 'Q08', number: 8, category: '일차식 덧셈', difficulty: 'medium',
    question: '(3x-2)+(x+5)를 계산하면?',
    choices: ['4x+3', '4x-3', '2x+3', '4x+7'],
    answer: 0,
    explanation: [
      '괄호 해제: 3x-2+x+5',
      'x항: 3x+x = 4x',
      '상수항: -2+5 = 3',
      '→ 4x+3 ✓',
    ],
    concept: '일차식 덧셈', misconceptionCode: 'AM08', learnStepId: 'AA05',
    misconception: '+( )는 괄호 그대로 해제하면 됩니다.',
  },
  {
    id: 'Q09', number: 9, category: '일차식 뺄셈', difficulty: 'medium',
    question: '(5x+3)-(2x-4)를 계산하면?',
    choices: ['3x-1', '3x+7', '7x-1', '3x-7'],
    answer: 1,
    explanation: [
      '-( )는 안의 부호 전부 반전',
      '5x+3-2x+4',
      'x항: 5x-2x = 3x, 상수항: 3+4 = 7',
      '→ 3x+7 ✓',
    ],
    concept: '일차식 뺄셈', misconceptionCode: 'AM09', learnStepId: 'AA05',
    misconception: '-(2x-4) = -2x+4! 빼기 뒤 괄호는 안의 부호 전체 반전.',
  },
  {
    id: 'Q10', number: 10, category: '분배법칙', difficulty: 'medium',
    question: '3(2x-5)를 계산하면?',
    choices: ['6x-5', '6x-15', '6x+15', '5x-15'],
    answer: 1,
    explanation: [
      '분배법칙: 3을 괄호 안 모두에 곱함',
      '3×2x = 6x, 3×(-5) = -15',
      '→ 6x-15 ✓',
    ],
    concept: '분배법칙', misconceptionCode: 'AM10', learnStepId: 'AA06',
    misconception: '분배법칙: 3(2x-5) = 6x-15. 상수항에도 꼭 곱해야!',
  },
  {
    id: 'Q11', number: 11, category: '분배법칙(음수)', difficulty: 'medium',
    question: '-2(3x-4)를 계산하면?',
    choices: ['-6x-8', '-6x+8', '6x-8', '-6x+4'],
    answer: 1,
    explanation: [
      '-2×3x = -6x',
      '-2×(-4) = +8',
      '→ -6x+8 ✓',
    ],
    concept: '음수 분배', misconceptionCode: 'AM11', learnStepId: 'AA06',
    misconception: '-2×(-4) = +8! 음수×음수 = 양수',
  },
  {
    id: 'Q12', number: 12, category: '나눗셈', difficulty: 'medium',
    question: '(8x-4)÷4를 계산하면?',
    choices: ['2x-4', '2x-1', '8x-1', '4x-2'],
    answer: 1,
    explanation: [
      '(8x-4)÷4 = 8x/4 - 4/4',
      '= 2x - 1 ✓',
    ],
    concept: '일차식 나눗셈', misconceptionCode: 'AM10', learnStepId: 'AA06',
    misconception: '나눗셈: 각 항을 모두 나눕니다. 8x÷4=2x, -4÷4=-1',
  },
  {
    id: 'Q13', number: 13, category: '종합 계산', difficulty: 'hard',
    question: '2(3x-1)-3(x+2)를 계산하면?',
    choices: ['3x-8', '3x+8', '-3x-8', '3x-4'],
    answer: 0,
    explanation: [
      '2(3x-1) = 6x-2',
      '-3(x+2) = -3x-6',
      '합: 6x-2-3x-6 = 3x-8 ✓',
    ],
    concept: '일차식 혼합 계산', misconceptionCode: 'AM11', learnStepId: 'AA06',
    misconception: '분배 후 동류항 정리: (6x-3x)+(-2-6) = 3x-8',
  },
  {
    id: 'Q14', number: 14, category: '식의 값(응용)', difficulty: 'hard',
    question: 'x=2, y=-1일 때, 3x²-2xy+y의 값은?',
    choices: ['17', '15', '13', '11'],
    answer: 1,
    explanation: [
      '3×(2)² - 2×(2)×(-1) + (-1)',
      '= 3×4 + 4 + (-1)',
      '= 12 + 4 - 1 = 15 ✓',
    ],
    concept: '식의 값(다변수)', misconceptionCode: 'AM05', learnStepId: 'AA03',
    misconception: '각 문자에 괄호로 대입! (2)²=4, (-1) 주의',
  },
  {
    id: 'Q15', number: 15, category: '종합', difficulty: 'hard',
    question: '다음 식을 간단히 하면?\n4(x-2) - (3x-5) + 2(-x+1)',
    choices: ['-x-1', '-x+1', 'x-1', '-x-3'],
    answer: 0,
    explanation: [
      '4(x-2) = 4x-8',
      '-(3x-5) = -3x+5',
      '2(-x+1) = -2x+2',
      '합: 4x-8-3x+5-2x+2 = (4-3-2)x+(-8+5+2) = -x-1 ✓',
    ],
    concept: '일차식 종합 계산', misconceptionCode: 'AM09', learnStepId: 'AA05',
    misconception: '각 괄호를 분배하고 동류항 정리: (4-3-2)x=−x, (-8+5+2)=−1',
  },
]

interface Props { onGoLearn?: (stepId: string) => void }

export default function A1TestPage({ onGoLearn }: Props) {
  const [phase, setPhase] = useState<'intro' | 'test' | 'result'>('intro')
  const [current, setCurrent] = useState(0)
  const [answers, setAnswers] = useState<(number | null)[]>(Array(TEST_QUESTIONS.length).fill(null))
  const [showResult, setShowResult] = useState(false)
  const [seconds, setSeconds] = useState(0)
  const [expandedExpl, setExpandedExpl] = useState<Set<number>>(new Set())
  const [videoModal, setVideoModal] = useState<{ stepId: string; label: string } | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { recordTestResult, recordMisconceptions } = useProgressStore(s => ({
    recordTestResult: s.recordTestResult,
    recordMisconceptions: s.recordMisconceptions,
  }))
  const unitProgress = useUnitProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 3)
  const getApprovedByTag = useVideoStore(s => s.getApprovedByTag)

  useEffect(() => {
    if (phase === 'test') {
      intervalRef.current = setInterval(() => setSeconds(s => s + 1), 1000)
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [phase])

  const fmt = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`

  const handleSubmit = () => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    const correct = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
    const sc = Math.round((correct / TEST_QUESTIONS.length) * 100)
    const wrongCodes = TEST_QUESTIONS
      .filter((q, i) => answers[i] !== q.answer && q.misconceptionCode)
      .map(q => q.misconceptionCode!)
    if (wrongCodes.length > 0) recordMisconceptions(UNIT_ID, wrongCodes)
    recordTestResult(UNIT_ID, {
      unitId: UNIT_ID,
      score: sc,
      correctCount: correct,
      totalCount: TEST_QUESTIONS.length,
      durationSec: seconds,
      answers: answers as number[],
      wrongConceptIds: TEST_QUESTIONS.filter((q, i) => answers[i] !== q.answer).map(q => q.concept),
      grade: sc >= 90 ? 'A' : sc >= 80 ? 'B' : sc >= 70 ? 'C' : sc >= 60 ? 'D' : 'F',
      passed: sc >= PASS_SCORE,
    })
    setPhase('result')
  }

  const correctCount = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
  const score = Math.round((correctCount / TEST_QUESTIONS.length) * 100)
  const passed = score >= PASS_SCORE
  const wrongQuestions = TEST_QUESTIONS.filter((_, i) => answers[i] !== TEST_QUESTIONS[i].answer)
  const answeredCount = answers.filter(a => a !== null).length

  // ── 인트로 ──────────────────────────────────────────────────────────────────
  if (phase === 'intro') {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="card text-center">
          <div className="text-6xl mb-4">🔤</div>
          <h1 className="text-2xl font-black text-slate-800 mb-1">문자의 사용과 식 실전 TEST</h1>
          <p className="text-slate-500 mb-6 text-sm">A1-1 단원 전 범위 · {TEST_QUESTIONS.length}문항 · 25분</p>
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              { icon: <PenTool size={18} className="text-blue-500" />, label: '문항 수', value: `${TEST_QUESTIONS.length}문항` },
              { icon: <Clock size={18} className="text-orange-500" />, label: '권장 시간', value: '25분' },
              { icon: <Trophy size={18} className="text-yellow-500" />, label: '합격 기준', value: `${PASS_SCORE}점` },
            ].map(item => (
              <div key={item.label} className="bg-slate-50 rounded-xl p-3">
                <div className="flex justify-center mb-1">{item.icon}</div>
                <p className="text-xs text-slate-500">{item.label}</p>
                <p className="text-sm font-black text-slate-800">{item.value}</p>
              </div>
            ))}
          </div>
          <div className="text-left bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <p className="text-xs font-bold text-blue-700 mb-2">📋 출제 범위</p>
            <div className="grid grid-cols-2 gap-1 text-xs text-slate-600">
              {[
                'AA01 문자를 사용한 식',
                'AA02 곱셈·나눗셈 기호 생략',
                'AA03 식의 값 (대입)',
                'AA04 일차식·동류항 정리',
                'AA05 일차식의 덧셈·뺄셈',
                'AA06 분배법칙·곱셈·나눗셈',
              ].map(t => (
                <div key={t} className="flex items-center gap-1">
                  <span className="text-blue-400">•</span> {t}
                </div>
              ))}
            </div>
          </div>
          {unitProgress.testRecords.length > 0 && (
            <div className="mb-5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-left">
              <p className="text-xs font-bold text-amber-700 mb-1">📊 이전 응시 기록</p>
              <p className="text-sm font-black text-amber-800">
                최고 점수: {unitProgress.bestScore}점 {unitProgress.passed && '🏆 합격'}
              </p>
              <p className="text-xs text-amber-600">총 {unitProgress.testRecords.length}회 응시</p>
            </div>
          )}
          <button
            onClick={() => { setPhase('test'); setCurrent(0); setSeconds(0); setAnswers(Array(TEST_QUESTIONS.length).fill(null)) }}
            className="w-full py-4 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-black text-lg shadow-lg hover:shadow-xl transition-all"
          >
            시험 시작 →
          </button>
        </div>
      </div>
    )
  }

  // ── 시험 ────────────────────────────────────────────────────────────────────
  if (phase === 'test') {
    const q = TEST_QUESTIONS[current]
    const answered = answers[current]

    return (
      <div className="p-4 max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-500">{current + 1} / {TEST_QUESTIONS.length}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
              q.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
              q.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
              'bg-red-100 text-red-700'
            }`}>
              {q.difficulty === 'easy' ? '쉬움' : q.difficulty === 'medium' ? '보통' : '어려움'}
            </span>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-mono font-bold text-sm bg-slate-100 text-slate-700">
            <Clock size={14} /> {fmt(seconds)}
          </div>
        </div>
        <div className="bg-slate-100 rounded-full h-1.5 mb-5 overflow-hidden">
          <div className="h-full bg-blue-500 rounded-full transition-all duration-300"
            style={{ width: `${((current + 1) / TEST_QUESTIONS.length) * 100}%` }} />
        </div>
        <div className="card mb-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-bold text-slate-400">{q.category}</span>
          </div>
          <p className="text-base font-bold text-slate-800 leading-relaxed mb-5 whitespace-pre-line">
            {q.number}. {q.question}
          </p>
          <div className="space-y-2.5">
            {q.choices.map((choice, i) => (
              <button key={i}
                onClick={() => { const next = [...answers]; next[current] = i; setAnswers(next) }}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 font-medium text-sm transition-all ${
                  answered === i
                    ? 'border-blue-500 bg-blue-50 text-blue-800 shadow-sm'
                    : 'border-slate-200 bg-white hover:border-blue-300 text-slate-700'
                }`}
              >
                <span className="font-black mr-2 text-slate-400">{['①','②','③','④'][i]}</span>{choice}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <button disabled={current === 0} onClick={() => setCurrent(c => c - 1)}
            className="flex-1 py-2.5 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold text-sm disabled:opacity-40 hover:bg-slate-50">
            ← 이전
          </button>
          {current < TEST_QUESTIONS.length - 1 ? (
            <button onClick={() => setCurrent(c => c + 1)}
              className="flex-1 py-2.5 rounded-xl bg-blue-500 text-white font-bold text-sm hover:bg-blue-600">
              다음 →
            </button>
          ) : (
            <button onClick={handleSubmit}
              className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-black text-sm hover:opacity-90">
              제출하기 ✓
            </button>
          )}
        </div>
        <div className="mt-4 flex flex-wrap gap-1.5 justify-center">
          {TEST_QUESTIONS.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)}
              className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
                i === current ? 'bg-blue-500 text-white' :
                answers[i] !== null ? 'bg-emerald-100 text-emerald-700' :
                'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}>
              {i + 1}
            </button>
          ))}
        </div>
        <p className="text-center text-xs text-slate-400 mt-2">
          답변 완료: {answeredCount}/{TEST_QUESTIONS.length}
        </p>
      </div>
    )
  }

  // ── 결과 ────────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 max-w-3xl mx-auto space-y-5">
      <div className={`card text-center border-2 ${passed ? 'border-emerald-300 bg-emerald-50' : 'border-orange-300 bg-orange-50'}`}>
        <div className="text-5xl mb-3">{passed ? '🏆' : '📚'}</div>
        <h2 className="text-3xl font-black mb-1" style={{ color: passed ? '#059669' : '#ea580c' }}>{score}점</h2>
        <p className={`text-base font-bold mb-4 ${passed ? 'text-emerald-700' : 'text-orange-700'}`}>
          {passed ? '🎉 합격! 문자의 사용과 식 완전 마스터!' : `아직 ${PASS_SCORE - score}점 부족해요. 다시 도전!`}
        </p>
        <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto mb-5">
          {[
            { label: '정답', value: `${correctCount}문항`, color: 'text-emerald-600' },
            { label: '오답', value: `${TEST_QUESTIONS.length - correctCount}문항`, color: 'text-red-500' },
            { label: '소요 시간', value: fmt(seconds), color: 'text-slate-600' },
          ].map(item => (
            <div key={item.label} className="bg-white rounded-xl p-2">
              <p className={`text-lg font-black ${item.color}`}>{item.value}</p>
              <p className="text-xs text-slate-500">{item.label}</p>
            </div>
          ))}
        </div>
        <button
          onClick={() => {
            setPhase('intro')
            setAnswers(Array(TEST_QUESTIONS.length).fill(null))
            setSeconds(0)
            setShowResult(false)
            setExpandedExpl(new Set())
          }}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border-2 border-slate-200 text-slate-700 font-bold text-sm hover:bg-slate-50"
        >
          <RotateCcw size={14} /> 다시 풀기
        </button>
      </div>

      {/* AI 취약점 분석 */}
      {predictedFragments.length > 0 && (
        <div className="card border-2 border-violet-200 bg-violet-50">
          <div className="flex items-center gap-2 mb-3">
            <Brain size={16} className="text-violet-600" />
            <span className="text-sm font-black text-violet-800">🤖 AI 취약점 분석</span>
          </div>
          <div className="space-y-2">
            {predictedFragments.map(pred => {
              const tagConf = FRAGMENT_TAG_CONFIG[pred.tag]
              return (
                <div key={pred.fragment.id} className={`rounded-xl border ${tagConf.color} p-3`}>
                  <div className="flex items-start gap-2">
                    <span className="text-lg">{pred.fragment.emoji}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${tagConf.badge}`}>{tagConf.label}</span>
                        <span className="text-xs font-mono text-slate-500">{pred.fragment.misconceptionCode}</span>
                      </div>
                      <p className="text-xs font-bold text-slate-800">{pred.fragment.title}</p>
                      <p className="text-xs text-slate-600 mt-0.5">{pred.fragment.fixTip}</p>
                      {onGoLearn && (
                        <button onClick={() => onGoLearn(pred.fragment.stepId)}
                          className="mt-2 text-xs px-2.5 py-1 rounded-lg bg-violet-600 text-white font-bold hover:bg-violet-700">
                          → {pred.fragment.stepTitle} 학습
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* 오답 노트 */}
      {wrongQuestions.length > 0 && (
        <div className="card">
          <button onClick={() => setShowResult(r => !r)} className="w-full flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-orange-500" />
              <span className="text-sm font-black text-slate-800">오답 노트 ({wrongQuestions.length}문제)</span>
            </div>
            {showResult ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
          </button>
          {showResult && (
            <div className="mt-4 space-y-4">
              {wrongQuestions.map(q => {
                const qIdx = TEST_QUESTIONS.indexOf(q)
                const myAns = answers[qIdx]
                const isExpanded = expandedExpl.has(qIdx)
                const video = getApprovedByTag(q.learnStepId ?? '')
                return (
                  <div key={q.id} className="border-2 border-red-100 rounded-xl overflow-hidden">
                    <div className="bg-red-50 px-4 py-3">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs font-black text-red-600">문제 {q.number}</span>
                        <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full">{q.category}</span>
                        {q.misconceptionCode && (
                          <span className="text-xs font-mono bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full">
                            {q.misconceptionCode}
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-slate-800 whitespace-pre-line">{q.question}</p>
                    </div>
                    <div className="px-4 py-3 space-y-2">
                      <div className="grid grid-cols-2 gap-1.5">
                        {q.choices.map((c, i) => (
                          <div key={i} className={`text-xs px-2.5 py-1.5 rounded-lg flex items-center gap-1 ${
                            i === q.answer
                              ? 'bg-emerald-100 text-emerald-800 font-bold border border-emerald-300'
                              : i === myAns
                                ? 'bg-red-100 text-red-700 border border-red-200'
                                : 'bg-slate-50 text-slate-500'
                          }`}>
                            {i === q.answer && <CheckCircle size={10} className="text-emerald-600 flex-shrink-0" />}
                            {i === myAns && i !== q.answer && <XCircle size={10} className="text-red-500 flex-shrink-0" />}
                            {['①','②','③','④'][i]} {c}
                          </div>
                        ))}
                      </div>
                      {q.misconception && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                          <p className="text-xs font-bold text-amber-700">⚠️ 오개념 경고</p>
                          <p className="text-xs text-amber-800 mt-0.5">{q.misconception}</p>
                        </div>
                      )}
                      <button
                        onClick={() => setExpandedExpl(s => {
                          const n = new Set(s); n.has(qIdx) ? n.delete(qIdx) : n.add(qIdx); return n
                        })}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 font-semibold"
                      >
                        <Lightbulb size={11} />{isExpanded ? '해설 닫기' : '해설 보기'}
                      </button>
                      {isExpanded && (
                        <div className="bg-slate-50 rounded-xl p-3 space-y-1">
                          {q.explanation.map((line, i) => (
                            <p key={i} className="text-xs text-slate-700 leading-relaxed">{line}</p>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-2 flex-wrap">
                        {onGoLearn && q.learnStepId && (
                          <button onClick={() => onGoLearn(q.learnStepId!)}
                            className="text-xs px-3 py-1.5 rounded-lg bg-blue-500 text-white font-bold hover:bg-blue-600 flex items-center gap-1">
                            <BookOpen size={11} /> 개념 다시 보기 ({q.learnStepId})
                          </button>
                        )}
                        {video && (
                          <button onClick={() => setVideoModal({ stepId: q.learnStepId!, label: q.concept })}
                            className="text-xs px-3 py-1.5 bg-red-100 text-red-700 rounded-lg font-semibold hover:bg-red-200 flex items-center gap-1">
                            <Youtube size={11} /> 영상
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* 전체 답안 */}
      <div className="card">
        <div className="flex items-center gap-2 mb-3">
          <BarChart2 size={16} className="text-slate-500" />
          <span className="text-sm font-bold text-slate-700">전체 답안 확인</span>
        </div>
        <div className="grid grid-cols-5 gap-1.5">
          {TEST_QUESTIONS.map((q, i) => {
            const correct = answers[i] === q.answer
            return (
              <div key={i} className={`rounded-lg p-2 text-center ${
                correct ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'
              }`}>
                <p className="text-xs font-bold text-slate-500">{i + 1}번</p>
                {correct
                  ? <CheckCircle size={14} className="text-emerald-500 mx-auto" />
                  : <XCircle size={14} className="text-red-500 mx-auto" />}
              </div>
            )
          })}
        </div>
      </div>

      {videoModal && (
        <ConceptVideoModal
          conceptTag={videoModal.stepId}
          conceptLabel={videoModal.label}
          viewerId="student_001"
          onClose={() => setVideoModal(null)}
          onCompleted={() => setVideoModal(null)}
        />
      )}
    </div>
  )
}
